import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Table,
  Space,
  Tag,
  notification,
  Button,
  Row,
  Col,
  Card,
} from "antd";
import { adminUserService } from "../services/adminUserService";
import { userKcService } from "../services/UserKcService";
import styles from "./UserReport.module.css";
import FilterPanel, { FilterConfig } from "../components/common/FilterPanel";
import { useNavigate } from "react-router-dom";
import { useLoaderProvider } from "../contexts/LoaderContext";
import { AdminUserDTO } from "../models/AdminUser";
import { Popconfirm } from "antd/lib";
import { PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { routes } from "../routes";

const { Title } = Typography;

const UserReport: React.FC = () => {
  const { showLoader, hideLoader } = useLoaderProvider();
  const [users, setUsers] = useState<AdminUserDTO[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const navigate = useNavigate();

  const pageSize = 25;

  const handleViewUser = (userId: string) => {
    // navigate(routes.USER_DETAILS.replace(":id", userId));
  };

  const handleActivateUser = async (userId: string) => {
    try {
      showLoader("Attivazione utente in corso...");
      await userKcService.activateUser(userId);
      notification.success({ message: "Utente attivato con successo" });
      fetchUsers(currentPage);
    } catch (error) {
      notification.error({ message: "Errore nell'attivazione dell'utente" });
      console.error("Errore nell'attivazione dell'utente:", error);
    } finally {
      hideLoader();
    }
  };

  const filterConfig: FilterConfig[] = [
    {
      name: "login.contains",
      label: "Username",
      type: "input",
    },
    {
      name: "email.contains",
      label: "Email",
      type: "input",
    },
    {
      name: "activated.equals",
      label: "Status",
      type: "select",
      options: [
        { value: "true", label: "Attivo" },
        { value: "false", label: "Inattivo" },
      ],
    },
  ];

  const columns = [
    {
      title: "Username",
      dataIndex: "login",
      key: "login",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Nome",
      dataIndex: "firstName",
      key: "firstName",
    },
    {
      title: "Cognome",
      dataIndex: "lastName",
      key: "lastName",
    },
    {
      title: "Stato",
      dataIndex: "activated",
      key: "activated",
      render: (activated: boolean) => (
        <Tag color={activated ? "green" : "red"}>
          {activated ? "Attivo" : "Inattivo"}
        </Tag>
      ),
    },
    {
      title: "Ruoli",
      key: "authorities",
      dataIndex: "authorities",
      render: (authorities: string[]) => (
        <>
          {authorities.map((authority) => (
            <Tag color="blue" key={authority}>
              {authority}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Azioni",
      key: "actions",
      render: (_: any, record: AdminUserDTO) => (
        <Space size="small">
          {/* <Button type="default" onClick={() => handleViewUser(record.id!)}>
            Visualizza
          </Button> */}
          {!record.activated && (
            <Popconfirm
              title="Attivare l'utente"
              description="Sei sicuro di voler attivare questo utente?"
              onConfirm={() => handleActivateUser(record.id!)}
              okText="Si"
              cancelText="No"
            >
              <Button type="primary">Attiva</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const fetchUsers = useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        showLoader("Caricamento utenti...");
        const params = {
          page: page - 1,
          size: pageSize,
          sort: ["id,asc"],
          ...filters,
        } as any;

        const [items, count] = await Promise.all([
          adminUserService.getManagedUsers(params),
          adminUserService.countUsers(params),
        ]);

        setUsers(items);
        setTotalItems(count);
      } catch (error) {
        notification.error({ message: "Errore nel caricamento degli utenti" });
        console.error("Errore nel caricamento degli utenti:", error);
      } finally {
        setLoading(false);
        hideLoader();
      }
    },
    [filters, showLoader, hideLoader]
  );

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage, filters, fetchUsers]);

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
  };

  const handleFilterChange = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  return (
    <div className={styles.reportContainer}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2}>Report Utenti</Title>
          </Col>
          <Col>
            <Link to={`${routes.CREATE_USER}`}>
              <Card className={styles.createUserCard} hoverable>
                <PlusOutlined /> CREA UTENTE
              </Card>
            </Link>
          </Col>
        </Row>
        <FilterPanel
          filters={filterConfig}
          onFilterChange={handleFilterChange}
          showExport={false}
        />
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalItems,
            showSizeChanger: false,
          }}
          onChange={handleTableChange}
          scroll={{ x: true }}
        />
      </Space>
    </div>
  );
};

export default UserReport;
